import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { GridOptions, RowNode } from 'ag-grid';
import { DriverScheduleService } from './driver-schedule.service';
import { UsersService } from '../users/users.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { BooleanCheckComponent } from '../A2-utils/grid-components/boolean-check/boolean-check.component';
import { ActivatedRoute } from '@angular/router';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { anchorPopupToSource } from '../../core/helpers/ag-grid-popup.util';
import { Subscription } from 'rxjs/Subscription';
import { ScheduleSortChange } from './driver-schedule.service';
import {
  DriverScheduleFilterModel,
  DriverScheduleSortModel,
  sliceResourcesGridFilterModel,
  toDriverScheduleFilterModel
} from './driver-schedule-filter.model';

declare const moment: any;

@Component({
  selector: 'app-driver-schedule',
  templateUrl: './driver-schedule.component.html',
  styleUrls: ['./driver-schedule.component.scss']
})
export class DriverScheduleComponent implements OnInit, OnDestroy {

  @ViewChild('addSchedule') addScheduleModal: T2CardComponent;
  @ViewChild('days') daysDiv: ElementRef;
  @ViewChild('scrollBar') scrollBar: ElementRef;

  filterModel: DriverScheduleFilterModel = new DriverScheduleFilterModel();
  sortModel: DriverScheduleSortModel[] = [];

  dateRange: any;
  myDatePickerOptions = {
    dateFormat: 'yyyy-mm-dd',
    editableDateRangeField: false
  };
  driversGrid: GridOptions;
  dates = [];
  selectedProvider = 19;
  providers = [];
  copiedDate: any;
  tempCopiedDate: any;
  showSchedulePanel = false;
  private subscriptions: Subscription[] = [];
  private syncingFromScheduleGrid = false;
  private scheduleVisibleDriverIdSet: Set<number> | null = null;
  private format = (date): string => {
    return date.year + '-'
        + (date.month < 10 ? '0' : '')
        + date.month + '-'
        + (date.day < 10 ? '0' : '')
        + date.day
  }

  constructor(
    private service: DriverScheduleService,
    private userService: UsersService,
    private loading: T2LoadingService,
    private activatedRoute : ActivatedRoute,
    private toastrAlertHelper: ToastrAlertHelper,
    private sweetAlertHelper: SweetAlertHelper,
  ) {
    this.driversGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      gridAutoHeight: true,
      suppressHorizontalScroll: true,
      columnDefs: this.getColumnDefs(),
      postProcessPopup: (params) => {
        if (params && params.ePopup) {
          anchorPopupToSource(
            params.ePopup as HTMLElement,
            params.eventSource as HTMLElement,
          );
        }
      },
      isExternalFilterPresent: () => !!this.scheduleVisibleDriverIdSet,
      doesExternalFilterPass: (node: RowNode) => this.doesDriverPassScheduleExternalFilter(node),
      onGridReady: (event) => {
        if (event && event.api) {
          event.api.sizeColumnsToFit();
        }
        if (localStorage.getItem('selectedScheduleDate')) {
          const date = JSON.parse(localStorage.getItem('selectedScheduleDate'));
          this.retrieveData(this.format(date), this.format(date));
          localStorage.removeItem('selectedScheduleDate');
        }
      },
      onFilterChanged: () => {
        this.onFilterChanged();
      },
      onSortChanged: () => {
        this.onSortChanged();
      }
    };
   }


  ngOnInit() {
    this.getProviders();
    this.subscriptions.push(
      this.activatedRoute.params.subscribe((params: any) => {
        if (params.id) {
          this.selectedProvider = params.id;
        }
      })
    );

    this.setDateFromStorage();
    this.subscribeToScheduleEvents();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  private subscribeToScheduleEvents(): void {
    this.subscriptions.push(
      this.service.reload$.subscribe({
        next: () => {
          this.onReloadData();
        }
      }),
      this.service.dateSelection$.subscribe({
        next: (event) => {
          this.onSelectDate(event);
        }
      }),
      this.service.scheduleSortChanged$.subscribe({
        next: (event: ScheduleSortChange) => {
          this.onScheduleSortChanged(event);
        }
      }),
      this.service.scheduleFilterModelChanged$.subscribe({
        next: (filterModel) => {
          this.onScheduleFilterModelChanged(filterModel);
        }
      })
    );
  }

  private setDateFromStorage() {
    if (localStorage.getItem('selectedScheduleDate')) {
      const date = JSON.parse(localStorage.getItem('selectedScheduleDate'));
      this.dateRange = {
        beginDate: date,
        endDate: date,
        formatted: `${this.format(date)} - ${this.format(date)}`
      };
      this.showSchedulePanel = true;
    }
  }

  /**
   * this get data from server
   */
  private retrieveData(start, end) {
    if (this.selectedProvider) {
      this.loading.show();
      this.service.getSchedules(start, end, this.selectedProvider).subscribe(
        res => {
          this.filterModel = Object.assign(
            {},
            sliceResourcesGridFilterModel(this.filterModel),
            toDriverScheduleFilterModel(this.driversGrid.api.getFilterModel())
          ) as DriverScheduleFilterModel;
          this.sortModel = this.driversGrid.api.getSortModel();
          if (this.driversGrid && this.driversGrid.api) {
            this.sortModel = this.driversGrid.api.getSortModel();
          }
          this.driversGrid.api.setRowData(res.drivers);
          this.driversGrid.api.setFilterModel(this.filterModel);
          this.driversGrid.api.setSortModel(this.sortModel);

          this.dates = res.dates;
          this.clearScheduleVisibleDriverFilter();
          this.synchronizeScheduleSort();
          this.setDefaultDates();

          this.loading.hiden();
        }, err => {
          this.showError(err.message);
          this.loading.hiden();
        }
      );
    }
  }

  /**
   * this execute when user select a date
   * @param event date event
   */
  onSeletedDate(event) {
    const split = event.formatted.split(' - ');
    if (split.length > 1) {
      this.showSchedulePanel = true;
      this.retrieveData(split[0], split[1]);
    } else {
      this.showSchedulePanel = false;
    }
  }

  /**
   * this return column defs
   */
  private getColumnDefs() {
    const orderedSetFilterParams = this.getOrderedSetFilterParams();

    return  [
      {
        headerName: 'Id',
        field: 'driverid',
        colId: 'driverid',
        hide: true,
        suppressToolPanel: true,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'Driver',
        field: 'driver',
        colId: 'driver',
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'Loc.',
        field: 'haslocation',
        colId: 'location',
        cellRendererFramework: BooleanCheckComponent,
        width: 60,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      }
    ]
  }

  private getOrderedSetFilterParams(): { suppressSorting: boolean; comparator: (a: string, b: string) => number; cellHeight: number } {
    return {
      suppressSorting: false,
      comparator: (a: string, b: string) => this.compareFilterValues(a, b),
      cellHeight: 32
    };
  }

  private compareFilterValues(a: string, b: string): number {
    const valueA = !a ? '' : String(a);
    const valueB = !b ? '' : String(b);
    return valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' });
  }

  private getDriverSortIndex(orderByDriverId: { [driverId: number]: number }, driverId: number): number {
    if (!orderByDriverId || driverId == null) {
      return Number.MAX_VALUE;
    }
    const order = orderByDriverId[driverId];
    return typeof order === 'number' ? order : Number.MAX_VALUE;
  }

  private getProviders() {
    this.userService.getAllProviders().subscribe(
      data => {
        this.providers = data;
      }
    )
  }

  onReloadData() {
    if (this.dateRange) {
      if (this.dateRange.formatted) {
        const split = this.dateRange.formatted.split(' - ');
        if (split.length > 1) {
          this.retrieveData(split[0], split[1]);
        }
      }
    }
  }

  onAddedSchedule(event) {
    this.addScheduleModal.toggleOpen();
    this.selectedProvider = event.providerid;
    this.retrieveData(event.fromdate, event.todate);

    const endDate = moment(event.todate);
    const startDate = moment(event.fromdate);
    this.dateRange = {
      beginDate: {
        year: startDate.year(),
        month: startDate.month() + 1,
        day:  startDate.date()
      },
      endDate: {
        year: endDate.year(),
        month: endDate.month() + 1,
        day: endDate.date()
      },
      formatted: `${event.fromdate} - ${event.todate}`
    };
  }

  onFilterChanged() {
    this.filterModel = Object.assign(
      {},
      sliceResourcesGridFilterModel(this.filterModel),
      toDriverScheduleFilterModel(this.driversGrid.api.getFilterModel())
    ) as DriverScheduleFilterModel;
    this.synchronizeScheduleSort();
  }

  onScheduleFilterModelChanged(scheduleFilterModel: DriverScheduleFilterModel) {
    this.filterModel = Object.assign(
      {},
      toDriverScheduleFilterModel(this.driversGrid.api.getFilterModel()),
      scheduleFilterModel || {}
    ) as DriverScheduleFilterModel;
  }

  onSortChanged() {
    if (!this.driversGrid || !this.driversGrid.api) {
      return;
    }

    if (this.syncingFromScheduleGrid) {
      return;
    }

    this.sortModel = this.driversGrid.api.getSortModel();
    this.synchronizeScheduleSort();
  }

  onScheduleSortChanged(event) {
    if (!event || !this.driversGrid || !this.driversGrid.api) {
      return;
    }
    if (!Array.isArray(event.orderedDriverIds)) {
      return;
    }

    this.sortModel = event.sortModel || [];

    this.syncingFromScheduleGrid = true;
    this.driversGrid.api.setSortModel([]);
    this.applyOrderByDriverIds(event.orderedDriverIds);
    this.updateScheduleVisibleDriverFilter(event.orderedDriverIds);
    setTimeout(() => {
      this.syncingFromScheduleGrid = false;
    }, 0);
  }

  private getTotalResourceDriverCount(): number {
    let total = 0;
    this.driversGrid.api.forEachNode((node) => {
      if (node && node.data) {
        total += 1;
      }
    });
    return total;
  }

  private clearScheduleVisibleDriverFilter() {
    this.scheduleVisibleDriverIdSet = null;
    if (this.driversGrid && this.driversGrid.api) {
      this.driversGrid.api.onFilterChanged();
    }
  }

  private doesDriverPassScheduleExternalFilter(node: RowNode): boolean {
    if (!this.scheduleVisibleDriverIdSet) {
      return true;
    }
    if (!node || !node.data) {
      return false;
    }
    const id = node.data.driverid;
    return this.scheduleVisibleDriverIdSet.has(id);
  }

  private updateScheduleVisibleDriverFilter(orderedDriverIds: number[]) {
    if (!this.driversGrid || !this.driversGrid.api) {
      return;
    }
    const total = this.getTotalResourceDriverCount();
    if (total === 0) {
      this.scheduleVisibleDriverIdSet = null;
    } else if (orderedDriverIds.length === 0) {
      this.scheduleVisibleDriverIdSet = new Set<number>();
    } else if (orderedDriverIds.length === total) {
      this.scheduleVisibleDriverIdSet = null;
    } else {
      this.scheduleVisibleDriverIdSet = new Set<number>(orderedDriverIds);
    }
    this.driversGrid.api.onFilterChanged();
  }

  private synchronizeScheduleSort() {
    if (!this.driversGrid || !this.driversGrid.api || !this.dates || !this.dates.length) {
      return;
    }

    const orderedDrivers = [];
    this.driversGrid.api.forEachNodeAfterFilterAndSort((node) => {
      if (node && node.data) {
        orderedDrivers.push(node.data);
      }
    });

    if (!orderedDrivers.length) {
      return;
    }

    const orderByDriverId = {};
    orderedDrivers.forEach((driver, index) => {
      orderByDriverId[driver.driverid] = index;
    });

    this.dates = this.dates.map((date) => {
      const columns = (date.columns || []).slice().sort((a, b) => {
        return this.getDriverSortIndex(orderByDriverId, a.driverid) - this.getDriverSortIndex(orderByDriverId, b.driverid);
      });

      return Object.assign({}, date, { columns: columns });
    });
  }

  private applyOrderByDriverIds(orderedDriverIds) {
    if (!orderedDriverIds || !orderedDriverIds.length || !this.driversGrid || !this.driversGrid.api) {
      return;
    }

    const orderByDriverId = {};
    orderedDriverIds.forEach((driverId, index) => {
      orderByDriverId[driverId] = index;
    });

    const drivers = [];
    this.driversGrid.api.forEachNode((node) => {
      if (node && node.data) {
        drivers.push(node.data);
      }
    });

    drivers.sort((a, b) => {
      return this.getDriverSortIndex(orderByDriverId, a.driverid) - this.getDriverSortIndex(orderByDriverId, b.driverid);
    });

    this.driversGrid.api.setRowData(drivers);

    this.dates = this.dates.map((date) => {
      const columns = (date.columns || []).slice().sort((a, b) => {
        return this.getDriverSortIndex(orderByDriverId, a.driverid) - this.getDriverSortIndex(orderByDriverId, b.driverid);
      });

      return Object.assign({}, date, { columns: columns });
    });
  }

  /// ************* RESET *************

  /**
   * this show a confirmation alert to reset day
   */
  showConfirmationReset() {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Are you sure?',
      text: `You won't be able to revert this!`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'No, cancel!'
    }).then((result) => {
      if (result.value) {
        this.resetSchedule();
      }
    })
  }
  /**
   * this reset schedule to default value
   */
  resetSchedule() {
    if (this.tempCopiedDate) {
      const data = {
        resetdate: this.removeDayText(this.tempCopiedDate),
        providerid: this.selectedProvider
      }

      this.loading.show();
      this.service.resetSchedule(data).finally(() => {
        this.loading.hiden();
      }).subscribe((res) => {
        this.toastrAlertHelper.success(res.message);
        this.clearCopied();
        this.onReloadData();
      }, error => {
        this.showError(error.message);
      })
    }
  }

  /// ************* COPY *************

  /**
   * this copy selected date
   */
  copyDate() {
    if (this.tempCopiedDate) {
      this.copiedDate = this.tempCopiedDate;
      this.toastrAlertHelper.success('Copied: ' + this.copiedDate);
      this.setDefaultDates();
    }
  }

  /**
   * this clear copied date
   */
  clearCopied() {
    this.copiedDate = undefined;
    this.tempCopiedDate = undefined;
    this.setDefaultDates();
  }
  /**
   * this execute when user select a date
   * @param event app-schedule event
   */
  onSelectDate(event) {
    if (this.copiedDate) {

      this.dates = this.dates.map(date => {
        if (date.date === event.date) {
          date.selected = event.selected;
        }
        return date;
      });

    } else {
      this.dates = this.dates.map(date => {
        if (date.date === event.date) {
          date.selected = event.selected;
        } else {
          date.selected = false;
        }

        return date;
      });
      this.tempCopiedDate = event.selected ? event.date : undefined;
    }
  }

  /// ************* PASTE *************

  /**
   * this show a confirm alert to paste date
   */
  showConfirmPaste() {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Are you sure?',
      text: `You won't be able to revert this!`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, cancel!'
    }).then((result) => {
      if (result.value) {
        this.pasteCopiedDate();
      }
    })
  }

  /**
   * this paste copied date
   */
  pasteCopiedDate() {
    const pasteDates = this.dates.filter(date => date.selected)
    .map(date => this.removeDayText(date.date));
    if (pasteDates.length) {
      const data = {
        copydate: this.removeDayText(this.copiedDate),
        pastedates: pasteDates,
        providerid: this.selectedProvider
      }

      this.loading.show();
      this.service.pasteSchedule(data).finally(() => {
        this.loading.hiden();
      })
      .subscribe(res => {
        this.toastrAlertHelper.success(res.message);
        this.clearCopied();
        this.onReloadData();
      }, error => {
        this.showError(error.message);
      })
    } else {
      this.showError('Please select at least one date to paste');
    }
  }
  // ************** UTILITIES **************

  /**
   * this set all selected false
   */
  private setDefaultDates() {
    this.dates = this.dates.map(date => {
      date.selected = false;
      return date;
    });
  }

  /**
   * this show a sweet alert with a error
   */
  private showError(message) {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Error!',
      text: message,
      type: 'error'
    });
  }
  /**
   * this remove day text to a date
   * @param date date to remove day text
   */
  removeDayText(date) {
    const split = date.split(' - ');
    return split.length > 1 ? split[1] : '';
  }

  /**
   * this synchronize scroll
   */
  updateScroll() {
    const scrollOne = this.scrollBar.nativeElement;
    const scrollTwo = this.daysDiv.nativeElement;
    scrollTwo.scrollLeft = scrollOne.scrollLeft;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/driver-schedule/driver-schedule.component.ts